import React from 'react';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { Bug, Code, GitCommit, GitPullRequest } from 'lucide-react';
import useProjectCard from '../../hooks/useProjectCard';

const AnalyticsCard = ({ 
  icon: Icon, 
  label, 
  value, 
  change, 
  changeType = 'increasing', 
  comparison = 'vs last week',
  additionalInfo,
  iconColor = '#737373'
}) => {
  const isPositive = changeType === 'increasing';
  const changeColor = isPositive ? '#22C55E' : '#EF4444';
  const ChangeIcon = isPositive ? TrendingUpIcon : TrendingDownIcon;

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: '#F8FAFC',
        borderRadius: 2,
        border: '1px solid #E2E8F0',
        height: '100%',
      }}
    >
      {/* Header with icon, label, and trend/additional info */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon  size={18} color={iconColor} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: '#1f1f1f',
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 500,
            }}
          >
            {label}
          </Typography>
        </Box>
        
        {/* Trend indicator or additional info */}
        {change && (
          <Box
            sx={{ display: 'flex', alignItems: 'center' }}
            role="text"
            aria-label={`${isPositive ? 'Increase' : 'Decrease'} of ${change}`}
          >
            <ChangeIcon sx={{ color: changeColor, fontSize: '1rem', mr: 0.5 }} aria-hidden="true" />
            <Typography
              variant="body2"
              sx={{
                color: changeColor,
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {typeof change === 'number'
                ? change.toFixed(2)
                : (typeof change === 'string' && !isNaN(Number(change))
                    ? Number(change).toFixed(2)
                    : change)}%
            </Typography>
          </Box>
        )}
        
        {additionalInfo && !change && (
          <Typography
            variant="body2"
            sx={{
              color: '#737373',
              fontSize: '0.675rem',
              fontWeight: 400,
              lineHeight: '1.25rem',
            }}
          >
            {additionalInfo}
          </Typography>
        )}
      </Box>

      {/* Value */}
      <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#1f1f1f',
            fontSize: '1.5rem',
            lineHeight: '2rem',
            ml: 1
          }}
        >
          {value}
        </Typography>
      </Box>

      {/* Comparison text */}
      <Typography
        variant="caption"
        sx={{
          color: '#737373',
          fontSize: '0.75rem',
          lineHeight: '1rem',
          ml: 1
        }}
      >
        {comparison}
      </Typography>
    </Box>
  );
};

const ProjectAnalytics = ({ project }) => {
  const { analytics, loading, error } = useProjectCard(project.id);

  const issuesAdditionalInfo = 'Velocity improving';

  const codeQualityAdditionalInfo = 'vs target';
  const codeQualityTarget = 'target: 95%';
  const codeQualityValue = (
    <>
      {analytics?.code_quality ? analytics.code_quality.toFixed(1) : '0.0'}%
      <Typography component="span" variant="body2" sx={{ color: '#94A3B8', fontSize: '1rem', fontWeight: 400, ml: 1 }}>
        {' / 95%'}
      </Typography>
    </>
  );

  if (loading) {
    return (
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2, color: '#737373' }}>
          Loading analytics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="h4"
          sx={{
            color: '#1f1f1f',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 600,
            mb: 2,
          }}
        >
          Project Analytics
        </Typography>
        <Box sx={{ p: 3, bgcolor: '#FEF2F2', borderRadius: 2, border: '1px solid #FECACA' }}>
          <Typography variant="body2" sx={{ color: '#DC2626' }}>
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="h4"
        sx={{
          color: '#1f1f1f',
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          fontWeight: 600,
          mb: 2,
        }}
      >
        Project Analytics
      </Typography>

      <Grid container spacing={3}>
        {/* Top Row */}
        <Grid item xs={12} sm={6}>
          <AnalyticsCard
            icon={GitCommit}
            label="Commits This Week"
            value={analytics?.commits_last_7_days || 0}
            change={analytics?.change_percentage}
            changeType={analytics?.trend === 'increasing' ? 'increasing' : 'decreasing'}
            comparison="vs last week"
            iconColor="#737373"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <AnalyticsCard
            icon={GitPullRequest}
            label="PRs This Week"
            value={analytics?.prs_last_7_days || 0}
            change={analytics?.prs_change_percentage}
            changeType={analytics?.prs_trend === 'increasing' ? 'increasing' : 'decreasing'}
            comparison="vs last week"
            iconColor="#9333ea"
          />
        </Grid>

        {/* Bottom Row */}
        <Grid item xs={12} sm={6}>
          <AnalyticsCard
            icon={Bug}
            label="Issues Resolved"
            value={analytics?.issues_resolved || 0}
            additionalInfo={issuesAdditionalInfo}
            comparison="this week"
            iconColor="#737373"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <AnalyticsCard
            icon={Code}
            label="Code Quality"
            value={codeQualityValue}
            additionalInfo={codeQualityAdditionalInfo}
            comparison={codeQualityTarget}
            iconColor="#ea580c"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectAnalytics;